/**
 * Animated swimming dolphin — cute, on-brand, fully decorative.
 *
 * Renders an SVG dolphin that swims slowly across the page with subtle
 * up/down bobbing motion + a tail wiggle. Bubbles trail behind it.
 */
import { useMemo } from 'react';

const DolphinSvg = () => (
  <svg viewBox="0 0 120 64" xmlns="http://www.w3.org/2000/svg" className="dolphin-svg" aria-hidden="true">
    <defs>
      <linearGradient id="dolphinBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#7ad8ff" />
        <stop offset="55%" stopColor="#00b4d8" />
        <stop offset="100%" stopColor="#0078a0" />
      </linearGradient>
      <linearGradient id="dolphinBelly" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#cde9f4" stopOpacity="0.7" />
      </linearGradient>
    </defs>
    {/* Tail */}
    <g className="dolphin-tail">
      <path d="M104,30 q10,-12 16,-8 q-2,8 -10,12 q8,4 10,12 q-6,4 -16,-8 z" fill="url(#dolphinBody)" />
    </g>
    {/* Body */}
    <path d="M8,34 q12,-22 44,-22 q26,0 50,18 q-12,8 -28,10 q-12,2 -22,0 q-14,-2 -28,-2 q-10,0 -16,-4 z" fill="url(#dolphinBody)" />
    {/* Belly highlight */}
    <path d="M16,38 q14,-6 36,-6 q22,0 44,8 q-10,4 -22,4 q-12,0 -24,-2 q-14,-2 -28,-2 q-4,0 -6,-2 z" fill="url(#dolphinBelly)" />
    {/* Top fin */}
    <path d="M52,12 q4,-12 16,-10 q-2,8 -8,12 z" fill="url(#dolphinBody)" />
    {/* Side flipper */}
    <path d="M44,38 q-4,12 -16,12 q2,-10 12,-16 z" fill="#0086b3" opacity="0.85" />
    {/* Eye */}
    <circle cx="30" cy="26" r="2.3" fill="#0a1628" />
    <circle cx="29.3" cy="25.3" r="0.9" fill="#ffffff" />
    {/* Smile */}
    <path d="M18,32 q4,3 12,2" stroke="#0a1628" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.7" />
  </svg>
);

export const FloatingDolphin = () => {
  // Random delay so multiple instances stay out of sync
  const delay = useMemo(() => Math.random() * 8, []);
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-10 z-0 hidden md:block"
      aria-hidden="true"
      data-testid="floating-dolphin"
    >
      <div className="dolphin-swim relative w-[160px]" style={{ animationDelay: `-${delay}s` }}>
        {/* Bubbles trailing behind the tail (tail is on the right side of the SVG, dolphin swims leftward) */}
        <span className="absolute -right-4 top-3 h-2 w-2 rounded-full bg-cyan-200/55 dolphin-bubble" style={{ animationDelay: '0s' }} />
        <span className="absolute -right-10 top-8 h-1.5 w-1.5 rounded-full bg-cyan-100/45 dolphin-bubble" style={{ animationDelay: '1.2s' }} />
        <span className="absolute -right-14 top-1 h-1 w-1 rounded-full bg-cyan-100/40 dolphin-bubble" style={{ animationDelay: '2.4s' }} />
        <div className="dolphin-bob">
          <DolphinSvg />
        </div>
      </div>
    </div>
  );
};
